/**
 * InstallPrompt — "Install Revivo IQ on your phone" banner (Add-to-Home-Screen).
 *
 * - Android / desktop Chrome: captures the `beforeinstallprompt` event and shows
 *   a native Install button.
 * - iOS Safari: no programmatic install exists, so we show the manual steps
 *   (Share → Add to Home Screen).
 * - Never shows when already installed (standalone) or recently dismissed,
 *   and hides itself once the app is installed.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { X, Share, SquarePlus, Smartphone } from 'lucide-react';

const DISMISS_KEY = 'revivo_a2hs_dismissed_at';
const REMIND_DAYS = 7;

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);
const isPhoneSized = () => window.matchMedia('(max-width: 1024px)').matches;
const recentlyDismissed = () => {
  const ts = Number(localStorage.getItem(DISMISS_KEY) || 0);
  return ts > 0 && Date.now() - ts < REMIND_DAYS * 86400000;
};

const InstallPrompt = () => {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const ios = isIOS() && !isStandalone();

  const dismiss = useCallback(() => {
    localStorage.setItem(DISMISS_KEY, String(Date.now()));
    setShow(false);
  }, []);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return undefined;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      if (isPhoneSized()) setShow(true);
    };
    const onInstalled = () => dismiss();
    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);

    // iOS Safari has no beforeinstallprompt → reveal the manual steps after the
    // page settles.
    const timer = ios ? setTimeout(() => setShow(true), 2500) : undefined;
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
      if (timer) clearTimeout(timer);
    };
  }, [dismiss, ios]);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try { await deferred.userChoice; } catch (_) { /* ignore */ }
    setDeferred(null);
    dismiss();
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-[60] px-3 pb-3 safe-bottom pointer-events-none"
      data-testid="install-prompt"
    >
      <div className="pointer-events-auto mx-auto max-w-md rounded-lg border border-primary/30 bg-zinc-950/95 backdrop-blur-md shadow-[0_0_30px_rgba(204,255,0,0.12)] p-4">
        <div className="flex items-start gap-3">
          <img src="/icon-192.png" alt="Revivo IQ" className="w-11 h-11 rounded-lg shrink-0" />
          <div className="min-w-0 flex-1">
            <p className="font-secondary font-bold text-white text-base leading-tight">
              Install Revivo IQ
            </p>
            <p className="text-xs text-zinc-400 mt-0.5">
              Add it to your home screen — full-screen, faster, feels like a real app.
            </p>
          </div>
          <button
            onClick={dismiss}
            className="text-zinc-500 hover:text-white p-1 -mt-1 -mr-1 shrink-0"
            aria-label="Dismiss"
            data-testid="install-prompt-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {ios ? (
          <div className="mt-3 rounded-md bg-zinc-900/70 border border-zinc-800 px-3 py-2.5 text-sm text-zinc-300 flex items-center gap-2 flex-wrap">
            <span className="inline-flex items-center gap-1">
              Tap <Share className="w-4 h-4 text-primary inline" />
            </span>
            <span className="inline-flex items-center gap-1">
              then <SquarePlus className="w-4 h-4 text-primary inline" />
              <span className="text-white font-medium">Add to Home Screen</span>
            </span>
          </div>
        ) : (
          <div className="mt-3 flex items-center gap-2">
            <button
              onClick={install}
              className="hpme-btn-primary flex-1 flex items-center justify-center gap-2 text-sm py-2.5"
              data-testid="install-prompt-install-btn"
            >
              <Smartphone className="w-4 h-4" /> Install app
            </button>
            <button
              onClick={dismiss}
              className="px-4 py-2.5 text-sm text-zinc-400 hover:text-white"
              data-testid="install-prompt-later-btn"
            >
              Later
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default InstallPrompt;
